let players = [];
let isPlaying = false;
let isPausedManually = false;
let currentTrack = "";

export function setTrack(trackPath) {
  currentTrack = trackPath;
}

/**
 * Tries to create an Audio element and verify it can load the src.
 * Returns the Audio instance, or throws with a clear message.
 */
async function createAudioPlayer(src, deviceId) {
  return new Promise((resolve, reject) => {
    const audio = new Audio();

    // Must set these BEFORE src so events fire correctly
    audio.loop = true;
    audio.preload = "auto";

    const onError = () => {
      const code = audio.error?.code;
      const msg = audio.error?.message ?? "unknown";
      reject(
        new Error(
          `Audio failed to load "${src}" (MediaError ${code}: ${msg}). ` +
            `Make sure the file exists in /public/music/ and is a valid MP3.`
        )
      );
    };

    const onCanPlay = async () => {
      audio.removeEventListener("error", onError);
      audio.removeEventListener("canplay", onCanPlay);
      try {
        // setSinkId is only available in Chromium browsers
        if (deviceId && typeof audio.setSinkId === "function") {
          await audio.setSinkId(deviceId);
        }
        resolve(audio);
      } catch (sinkErr) {
        console.warn("[Music] setSinkId failed, falling back to default output:", sinkErr);
        resolve(audio); // still resolve — just plays on default device
      }
    };

    audio.addEventListener("error", onError);
    audio.addEventListener("canplay", onCanPlay);

    // Setting src triggers loading
    audio.src = src;
    audio.load();
  });
}

export async function playOnMultipleDevices(deviceIds) {
  if (isPlaying) return;

  stopAll(); // reset state first

  const errors = [];

  for (const id of deviceIds) {
    try {
      const audio = await createAudioPlayer(currentTrack, id);
      await audio.play();
      players.push(audio);
    } catch (err) {
      errors.push(err.message);
      console.error("[Music] Failed to start player for device", id, err);
    }
  }

  if (players.length === 0) {
    // All devices failed — surface the first error
    throw new Error(errors[0] ?? "Failed to play audio on any device.");
  }

  isPlaying = true;
  isPausedManually = false;

  if (errors.length > 0) {
    // Some devices failed but at least one is playing — log but don't throw
    console.warn("[Music] Some devices failed:", errors);
  }
}

export function stopAll() {
  players.forEach((a) => {
    try {
      a.pause();
      a.currentTime = 0;
      a.src = ""; // release the resource
    } catch (_) {}
  });
  players = [];
  isPlaying = false;
  isPausedManually = false;
}

export function pauseAll() {
  players.forEach((a) => {
    try {
      a.pause();
    } catch (_) {}
  });
  isPlaying = false;
  isPausedManually = true;
}

export function resumeAll() {
  players.forEach((a) => {
    try {
      a.play();
    } catch (_) {}
  });
  isPlaying = true;
  isPausedManually = false;
}

export function getIsPausedManually() {
  return isPausedManually;
}